import React, { useCallback, useEffect, useState, useMemo } from 'react';
import cx from 'classnames';
import Col from '../../../../../../components/GridSystem/Col';
import Row from '../../../../../../components/GridSystem/Row';
import Ref from '../../../../../../components/Ref/Ref';
import Content from '../../../../../../components/Content/Content';
import ProtectedPageSidebar from '../../../../../../components/Sidebar/ProtectedPageSidebar';
import styles from './ArchiveDashboard.module.scss';
import ArchiveCards from '../ArchiveCards/ArchiveCards';
import MobileHeader from '../../../../../../components/MobileHeader';
import { useResponsiveContext } from '../../../../../../context/ResponsiveContext';
import useFetchListWithInfiniteScroll from '../../../../../../hooks/useFetchListWithInfiniteScroll';
import VideoService from '../../../../../../service/VideoService';
import { useLibraryContext } from '../../../../../../context/LibraryContext';
import RightSidebarMenu from '../../../../../../components/RightVideoActionMenu/RightSidebarMenu';
import ContentPageHeader from '../../../../../../components/ContentPageHeader/ContentPageHeader';
import RecordNotFound from '../../../../../../components/_empty_state/RecordNotFound';
import LocalStorageService from '../../../../../../service/LocalStorageService';
import FoldersList from '../../../FoldersList/FoldersList';
import useFetchFoldersList from '../../../../../../hooks/useFetchFoldersList';
import { useUserContext } from '../../../../../../context/UserContext';
import { LoadingSpinner } from '../../../../../../components/Icon/icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import Tabs from '../../../Tabs/Tabs';
import MobileSearchModal from '../../../../../../components/MobileSearchModal/MobileSearchModal';
import SearchModalContent from '../../../../../../components/SearchModalContent/SearchModalContent';

const viewModeToken = { GRID_VIEW: 'GRID_VIEW', LIST_VIEW: 'LIST_VIEW' };
const viewModeInit = viewModeToken.GRID_VIEW;

export default function ArchiveDashboard() {
  const [viewMode, setViewMode] = useState(viewModeInit);
  const isListView = useMemo(() => viewMode === viewModeToken.LIST_VIEW, [viewMode]);

  const { user, appConfig } = useUserContext();
  const { isMdAndBelow } = useResponsiveContext();
  const { selectedCard, selectedFolder } = useLibraryContext();
  const [searchModal, setSearchModal] = useState(false);
  const selectedVideoCount = Object.keys(selectedCard).length + Object.keys(selectedFolder).length;

  const [searchValue, setSearchValue] = useState({ query: '', tags: [] });

  // Such an object instead of a regular string is needed in order
  // to make the Tabs component more universal, since we also use it
  // in DashboardWithState.js where we specify a similar state for the location
  const [tab, setTab] = useState({ folders: false, team: false });

  const history = useHistory();

  const { userFullName, userAvatar } = useMemo(() => {
    if (user) {
      return { userFullName: user.userFullName, userAvatar: user.avatarUri };
    }
    return {};
  }, [user]);

  useEffect(() => {
    // Check app config
    if (!appConfig) {
      return;
    }

    let appConfigFeatures = appConfig.features;

    if (appConfigFeatures) {
      const featureWebAccess = appConfigFeatures.find(feature => feature.name === 'web_access');
      if (featureWebAccess) {
        if (!featureWebAccess.enabled) {
          history.push('/logout', { web_access_error: true });
          return;
        }
      }
    }
  }, [appConfig]);

  useEffect(() => {
    const savedViewMode = LocalStorageService.getItem('ViewMode');

    if (savedViewMode) {
      setViewMode(savedViewMode);
    }

    const isiOS =
      !navigator.userAgent.match('CriOS') &&
      (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !window.MSStream;

    function handleiIOSPopstate() {
      window.location.reload();
    }

    isiOS && window.addEventListener('popstate', handleiIOSPopstate);

    return () => {
      isiOS && window.removeEventListener('popstate', handleiIOSPopstate);
    };
  }, []);

  const handleClickTab = state => {
    setTab(state);
  };

  const getArchiveVideosWithStats = useCallback(async query => {
    query.archived = true;
    query.omitFiledStories = !query.searchTerm;
    query.include_empty = true;
    try {
      const response = await VideoService.getVideos(query);

      return new Promise(function (resolve) {
        return resolve(response);
      });
    } catch (err) {
      return new Promise(function (resolve, reject) {
        return reject(err);
      });
    }
  }, []);

  const {
    infiniteRef,
    list: videos,
    setList: setVideos,
    totalData,
    totalCurrent,
    isEmptyList: isVideosEmpty,
    loadList: loadVideos,
    isSearching,
    loading,
    searchInput: searchInputVideos,
    query,
    setTotalData,
  } = useFetchListWithInfiniteScroll({ service: getArchiveVideosWithStats, bottomMinOffset: -20 });

  const {
    list: folders,
    loading: folderLoading,
    loadList: loadFolders,
    query: folderQuery,
    searchInput: searchInputFolders,
  } = useFetchFoldersList({
    archived: true,
  });

  const onInputChange = value => {
    if (typeof value === 'string') {
      setSearchValue(prevState => ({ ...prevState, query: value }));
      if (tab?.folders || tab?.team) {
        searchInputFolders.onChange(value);
        return;
      }
      searchInputVideos.onChange('searchTerm', value);
    } else {
      setSearchValue(prevState => ({ ...prevState, tags: value }));
      searchInputVideos.onChange(
        'tags',
        value.map(item => item.id)
      );
    }
  };

  const closeSearchModal = () => {
    setSearchModal(false);
  };

  const openSearchModal = () => {
    setSearchModal(true);
  };

  const handleSetListView = useCallback(() => {
    if (!isListView) {
      setViewMode(viewModeToken.LIST_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.LIST_VIEW);
    }
  }, [isListView]);

  const handleSetGridView = useCallback(() => {
    if (isListView) {
      setViewMode(viewModeToken.GRID_VIEW);
      LocalStorageService.setItem('ViewMode', viewModeToken.GRID_VIEW);
    }
  }, [isListView]);

  const setVideosAndTotalData = useCallback(
    updatedVideos => {
      setVideos(updatedVideos);
      setTotalData(totalData - 1);
    },
    [setTotalData, setVideos, totalData]
  );

  const videoListTitle = useMemo(() => {
    if (isVideosEmpty) return null;
    else if (isSearching) return `Filtering ${totalCurrent} of ${totalData} videos`;
    return `Showing ${videos?.length} of ${totalData} videos`;
  }, [isSearching, isVideosEmpty, totalCurrent, totalData, videos]);

  const { t } = useTranslation();

  return (
    <>
      <ProtectedPageSidebar />
      <MobileHeader
        libraryMode={true}
        userAvatar={userAvatar}
        userFirstLetter={userFullName?.charAt(0)?.toUpperCase()}
        openSearchModal={openSearchModal}
      />
      <Content className={styles.content}>
        <div className={styles.wrapper}>
          <ContentPageHeader
            userFullName={userFullName}
            isMdAndBelow={isMdAndBelow}
            isListView={isListView}
            handleSetGridView={handleSetGridView}
            handleSetListView={handleSetListView}
            searchValue={searchValue}
            handleInputChange={onInputChange}
            title={t('archive.title')}
            mode="ARCHIVE"
            isVideosTab={!tab?.folders && !tab?.team}
            className={styles.headerArchive}
          />
          <Tabs state={tab} onClick={handleClickTab} isHideTeam />
          {/*Videos*/}
          {!tab?.folders && !tab?.team && (
            <>
              {loading ? (
                <h2>{t('general.loading')}</h2>
              ) : (
                !(query.searchTerm && videos?.length === 0 && !isVideosEmpty) && (
                  <h2>
                    {t('archive.archived_videos')} ({videos && totalData})
                  </h2>
                )
              )}
              <Col xs={12} className={cx(styles.videoLibrary)}>
                <Ref innerRef={infiniteRef}>
                  <Row className={styles.row}>
                    {(!loading || videos.length > 0) && (
                      <ArchiveCards
                        searchInput={searchValue}
                        videos={videos}
                        listMode={isListView}
                        setVideosAndTotalData={setVideosAndTotalData}
                        loadVideos={loadVideos}
                        loadFolders={loadFolders}
                        searchModal={searchModal}
                        closeSearchModal={closeSearchModal}
                      />
                    )}
                    {loading && videos.length > 0 && (
                      <div className={styles.spinnerContainer}>
                        <LoadingSpinner theme="light" />
                      </div>
                    )}
                  </Row>
                </Ref>
              </Col>
              {!loading && query.searchTerm && videos?.length === 0 && !isVideosEmpty && (
                <RecordNotFound searchTerm={query.searchTerm} />
              )}
            </>
          )}
          {/*Folders*/}
          {tab?.folders && (
            <>
              {!folderLoading && <h2>{t('archive.folders')}</h2>}
              <FoldersList
                folders={folders}
                listMode={isListView}
                loadFolders={loadFolders}
                loadVideos={loadVideos}
                archive={true}
                searchInput={searchValue.query}
                folderQuery={folderQuery}
                folderLoading={folderLoading}
              />
            </>
          )}
        </div>
        {selectedVideoCount > 0 ? <RightSidebarMenu selectedVideos={selectedVideoCount} isArchive /> : null}
        <MobileSearchModal open={searchModal} onClose={closeSearchModal}>
          <SearchModalContent onClose={closeSearchModal} archived={true} tab={tab} />
        </MobileSearchModal>
      </Content>
    </>
  );
}
